import { toast } from "sonner";

// Fallback copy method using textarea (for when Clipboard API is blocked)
function copyToClipboardFallback(text: string): boolean {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);

    return successful;
  } catch (err) {
    console.error("Fallback copy failed:", err);
    return false;
  }
}

function notifyCopyResult(success: boolean, successMessage: string | undefined, silent: boolean) {
  if (silent) return;
  if (success) {
    toast.success("Copied!", {
      description: successMessage ?? "Text copied to clipboard",
    });
  } else {
    toast.error("Copy Failed", {
      description: "Failed to copy to clipboard",
    });
  }
}

async function copyToClipboard(text: string, successMessage?: string, silent = false) {
  let success = false;

  try {
    // Try modern Clipboard API first
    await navigator.clipboard.writeText(text);
    success = true;
  } catch (err) {
    console.log("Clipboard API failed, trying fallback method:", err);
    // Use fallback method
    success = copyToClipboardFallback(text);
  }

  notifyCopyResult(success, successMessage, silent);
  return success;
}

async function copyHtmlToClipboard(
  html: string,
  plainText: string,
  successMessage?: string,
  silent = false
) {
  let success = false;
  try {
    const htmlBlob = new Blob([html], { type: "text/html" });
    const textBlob = new Blob([plainText], { type: "text/plain" });
    const clipboardItem = new ClipboardItem({
      "text/html": htmlBlob,
      "text/plain": textBlob,
    });
    await navigator.clipboard.write([clipboardItem]);
    success = true;
  } catch (err) {
    console.log("Clipboard API with HTML failed, trying fallback method with plain text:", err);
    success = copyToClipboardFallback(plainText);
  }

  notifyCopyResult(success, successMessage, silent);
  return success;
}

export const useClipboard = () => ({ copyToClipboard, copyHtmlToClipboard });
